import type { BillingHistoryEntry } from '~/gql/billing/billing'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { formatCents } from '~/pages/backoffice/admin/billing/money'
import { formatMonthLabel } from '~/pages/backoffice/admin/billing/month'

type Props = { rows: BillingHistoryEntry[] }

/**
 * Douze derniers mois d'activité et de montants.
 *
 * ⚠️ Les montants sont recalculés avec la formule COURANTE : changer de formule
 * réécrit donc tout l'historique affiché — d'où la requête pilotée par le parent,
 * qui la rejoue après un changement. Tant qu'il n'y a pas de factures réelles,
 * l'historique des formules n'est pas conservé ; c'est dit à l'écran.
 */
function BillingHistoryTable({ rows }: Props) {
  const { t, i18n } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('billing.history.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{t('billing.history.help')}</p>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.history.month')}</TableHead>
                <TableHead className="text-right">{t('billing.monthlyActive')}</TableHead>
                <TableHead className="text-right">{t('billing.learnerCount')}</TableHead>
                <TableHead className="text-right">{t('billing.history.amount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.month}>
                  <TableCell className="font-medium">
                    {formatMonthLabel(row.month, i18n.language)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {row.monthlyActive}
                  </TableCell>
                  {/* Compteur glissant 12 mois tel qu'il était ce mois-là. */}
                  <TableCell className="text-right tabular-nums">
                    {row.learnerCount}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCents(row.amountCents, i18n.language)}
                    <span className="ml-2 text-xs text-muted-foreground">{t(`billing.tier.${row.tier}`)}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

export default BillingHistoryTable
